import React, { useCallback, useEffect, useMemo, useState } from "react";
import { Card, Col, Row, Typography, Button, Empty, Spin } from "antd";
import {
  ApartmentOutlined,
  ClockCircleOutlined,
  ContainerOutlined,
  CreditCardOutlined,
  DollarOutlined,
  LinkOutlined,
  ReloadOutlined,
  RightOutlined,
  ShoppingOutlined,
  TeamOutlined,
  WalletOutlined,
} from "@ant-design/icons";
import { useNavigate } from "react-router-dom";
import API from "../services/api";
import { fetchPaymentSummary } from "../services/paymentsApi";
import { useAuth } from "../context/AuthContext";
import { canAccessRoute, roleLabel } from "../constants/permissions";

// One accent per module: a pastel badge behind the icon and the same colour
// repeated on the label underneath the count, so the two cannot drift apart
// as cards are added or reordered.
const MODULE_COLORS = {
  users: { bg: "#FEF0E2", fg: "#D97706" },
  plans: { bg: "#F1ECFE", fg: "#7C3AED" },
  customers: { bg: "#E3F7FA", fg: "#0891B2" },
  subscriptions: { bg: "#FFF1E0", fg: "#EA580C" },
  features: { bg: "#FDEAF0", fg: "#E11D48" },
  planFeatures: { bg: "#E8F9F0", fg: "#059669" },
};

const statIcons = {
  users: <TeamOutlined />,
  plans: <DollarOutlined />,
  customers: <ApartmentOutlined />,
  subscriptions: <ShoppingOutlined />,
  features: <ContainerOutlined />,
  planFeatures: <LinkOutlined />,
};

// Each module tile is a link into its page, so a role only sees the tiles for
// the pages it may open - that also keeps us from calling an endpoint it is
// forbidden from, which would otherwise fail the whole batch. `payments` is
// fetched alongside these (it feeds the top strip and the graph below) but is
// kept out of this list, which defines the Modules grid only.
const MODULE_SOURCES = [
  { key: "users", route: "/users", endpoint: "/users" },
  { key: "plans", route: "/plans", endpoint: "/plans" },
  { key: "customers", route: "/customers", endpoint: "/customers" },
  { key: "subscriptions", route: "/subscriptions", endpoint: "/subscriptions" },
  { key: "features", route: "/features", endpoint: "/features" },
  { key: "planFeatures", route: "/plan-features", endpoint: "/plan-features" },
];

const PAYMENTS_SOURCE = { key: "payments", route: "/payments", endpoint: "/payments" };

// Donut ring geometry, in the viewBox's own units. A fixed-degree gap (not a
// fixed pixel gap) keeps the separation between slices visually even however
// many modules a role ends up with.
const DONUT_RADIUS = 76;
const DONUT_STROKE = 32;
const DONUT_CIRCUMFERENCE = 2 * Math.PI * DONUT_RADIUS;
const DONUT_GAP_DEG = 3;
const DONUT_GAP_LENGTH = (DONUT_GAP_DEG / 360) * DONUT_CIRCUMFERENCE;

const emptyStats = (sources) =>
  sources.reduce((acc, source) => ({ ...acc, [source.key]: null }), {});

const greetingForHour = (hour) => {
  if (hour < 12) return "Good morning";
  if (hour < 17) return "Good afternoon";
  return "Good evening";
};

const displayNameFor = (user) => {
  const local = user?.email?.split("@")[0] || "";
  return local ? local.charAt(0).toUpperCase() + local.slice(1) : roleLabel(user);
};

const titleize = (key) =>
  key.replace(/([A-Z])/g, " $1").replace(/^./, (str) => str.toUpperCase());

const openableProps = (onOpen) => ({
  onClick: onOpen,
  role: "button",
  tabIndex: 0,
  onKeyDown: (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      onOpen();
    }
  },
});

/**
 * One of the three compact cards along the top: Collected, Outstanding and a
 * Payments count. `value` is already a rendered node (a Spin, a number or a
 * stack of per-currency lines), not a raw figure, because the two money cards
 * and the count card do not share a value shape.
 */
const MiniStatCard = ({ icon, colors, title, value, onOpen }) => (
  <Card
    className="app-page-card dashboard-mini-card"
    variant="borderless"
    hoverable
    styles={{ body: { padding: 18 } }}
    {...openableProps(onOpen)}
  >
    <div className="dashboard-mini-header">
      <span className="dashboard-mini-icon" style={{ background: colors.bg, color: colors.fg }}>
        {icon}
      </span>
      <span className="dashboard-mini-label">{title}</span>
    </div>
    <div className="dashboard-mini-value">{value}</div>
  </Card>
);

/**
 * One tile in the Modules grid: icon badge, count and coloured label stacked
 * on the left, a chevron pinned to the right to read as a link into the page.
 */
const ModuleCard = ({ icon, colors, value, label, loading, onOpen }) => (
  <Card
    className="app-page-card dashboard-module-card"
    variant="borderless"
    hoverable
    styles={{ body: { padding: 18 } }}
    {...openableProps(onOpen)}
  >
    <div className="dashboard-module-main">
      <span className="dashboard-module-icon" style={{ background: colors.bg, color: colors.fg }}>
        {icon}
      </span>
      {loading ? (
        <div className="dashboard-module-loading">
          <Spin size="small" />
        </div>
      ) : (
        <div className="dashboard-module-figure">
          <span className="dashboard-module-value">{value ?? 0}</span>
          <span className="dashboard-module-label" style={{ color: colors.fg }}>
            {label}
          </span>
        </div>
      )}
    </div>
    <RightOutlined className="dashboard-module-arrow" />
  </Card>
);

const Dashboard = () => {
  const navigate = useNavigate();
  const { user } = useAuth();

  const greeting = useMemo(() => greetingForHour(new Date().getHours()), []);
  const displayName = useMemo(() => displayNameFor(user), [user]);

  const moduleSources = useMemo(
    () => MODULE_SOURCES.filter((source) => canAccessRoute(user, source.route)),
    [user]
  );

  // The same gate the payments cards go through. The summary and list
  // endpoints are master_admin only, so a role without the page must never be
  // asked for either.
  const mayReadPayments = useMemo(() => canAccessRoute(user, "/payments"), [user]);

  const statSources = useMemo(
    () => (mayReadPayments ? [...moduleSources, PAYMENTS_SOURCE] : moduleSources),
    [moduleSources, mayReadPayments]
  );

  const [stats, setStats] = useState(() => emptyStats(statSources));
  const [paymentTotals, setPaymentTotals] = useState(null);
  const [loading, setLoading] = useState(true);
  // Shared by the donut slices and the legend rows, so hovering or focusing
  // either highlights both - the legend is the mark's dependable identity
  // channel, not a separate thing from it.
  const [hoveredKey, setHoveredKey] = useState(null);

  const fetchStats = useCallback(async () => {
    setLoading(true);
    try {
      const [responses, summary] = await Promise.all([
        Promise.all(
          statSources.map((source) => API.get(source.endpoint, { params: { page: 1, limit: 1 } }))
        ),
        // No filters: the dashboard figure is every payment, which is what the
        // Payment History page shows before anything is narrowed. Its failure
        // is swallowed on its own so a summary outage still leaves the counts
        // on screen.
        mayReadPayments
          ? fetchPaymentSummary({})
              .then((response) => response.data.data || null)
              .catch(() => null)
          : Promise.resolve(null),
      ]);

      setStats(
        statSources.reduce(
          (acc, source, index) => ({
            ...acc,
            [source.key]:
              responses[index].data.meta?.total ?? responses[index].data.data?.length ?? 0,
          }),
          {}
        )
      );
      setPaymentTotals(summary);
    } catch {
      // Request failures are already captured by the shared API interceptor.
    } finally {
      setLoading(false);
    }
  }, [statSources, mayReadPayments]);

  useEffect(() => {
    fetchStats();
  }, [fetchStats]);

  const statRoutes = useMemo(
    () => [...MODULE_SOURCES, PAYMENTS_SOURCE].reduce(
      (acc, source) => ({ ...acc, [source.key]: source.route }),
      {}
    ),
    []
  );

  // One line per currency, already formatted by the backend. A set spanning
  // two currencies gets a line each rather than one number that added them
  // together.
  const moneyLines = useCallback(
    (key) =>
      (paymentTotals?.totals || []).map((entry) => ({
        currency: entry.currency,
        value: entry[`${key}_display`],
      })),
    [paymentTotals]
  );

  const renderMoneyValue = (key) => {
    if (loading) return <Spin size="small" />;
    const lines = moneyLines(key);
    if (!lines.length) {
      // An em dash, not a zero: the totals are missing here, and "0
      // collected" is a claim this card cannot make on their behalf.
      return <span className="dashboard-mini-empty">—</span>;
    }
    return lines.map((line) => <div key={line.currency}>{line.value}</div>);
  };

  // Every module count, plus payments when the role can see it, as one whole
  // the donut below divides into slices.
  const overviewData = statSources.map((source) => ({
    key: source.key,
    name: titleize(source.key),
    value: stats[source.key] ?? 0,
  }));
  const totalRecords = overviewData.reduce((sum, item) => sum + item.value, 0);

  // One slice per module, in a fixed stroke-dasharray/rotate arrangement
  // rather than a library: each slice is shortened by half a gap on either
  // side so a constant-angle gap separates every pair, including the one
  // that wraps from the last slice back to the first.
  let cumulativeDeg = 0;
  const donutSlices = overviewData.map((item, index) => {
    const fraction = totalRecords > 0 ? item.value / totalRecords : 0;
    const startDeg = cumulativeDeg;
    cumulativeDeg += fraction * 360;
    const arcLength = fraction * DONUT_CIRCUMFERENCE;
    const visibleLength = item.value > 0 ? Math.max(arcLength - DONUT_GAP_LENGTH, 3) : 0;
    const percent = totalRecords > 0 ? (item.value / totalRecords) * 100 : 0;

    return {
      ...item,
      colorClass: `dashboard-donut-c${(index % 7) + 1}`,
      rotateDeg: startDeg - 90 + DONUT_GAP_DEG / 2,
      dasharray: `${visibleLength} ${DONUT_CIRCUMFERENCE - visibleLength}`,
      percentLabel: percent === 0 ? "0%" : percent < 1 ? "<1%" : `${Math.round(percent)}%`,
    };
  });

  const openOverviewItem = (key) => () => navigate(statRoutes[key]);
  const overviewKeyHandler = (key) => (event) => {
    if (event.key === "Enter" || event.key === " ") {
      event.preventDefault();
      navigate(statRoutes[key]);
    }
  };

  return (
    <div className="dashboard-page">
      <div className="dashboard-greeting">
        <Typography.Title level={4} style={{ marginBottom: 4 }}>
          {greeting}, {displayName} <span role="img" aria-label="waving hand">👋</span>
        </Typography.Title>
        <Typography.Text type="secondary">
          Here&apos;s what&apos;s happening across your team today.
        </Typography.Text>
      </div>

      {mayReadPayments && (
        <Row gutter={[16, 16]} className="dashboard-mini-row">
          <Col xs={24} sm={8}>
            <MiniStatCard
              icon={<WalletOutlined />}
              colors={{ bg: "#E8EEFE", fg: "#4B7CF3" }}
              title="Collected"
              value={renderMoneyValue("collected")}
              onOpen={() => navigate("/payments")}
            />
          </Col>
          <Col xs={24} sm={8}>
            <MiniStatCard
              icon={<ClockCircleOutlined />}
              colors={{ bg: "#FFF1E0", fg: "#F59E0B" }}
              title="Outstanding"
              value={renderMoneyValue("outstanding")}
              onOpen={() => navigate("/payments")}
            />
          </Col>
          <Col xs={24} sm={8}>
            <MiniStatCard
              icon={<CreditCardOutlined />}
              colors={{ bg: "#E8F9F0", fg: "#16A34A" }}
              title="Payments"
              value={loading ? <Spin size="small" /> : stats.payments ?? 0}
              onOpen={() => navigate("/payments")}
            />
          </Col>
        </Row>
      )}

      <div className="dashboard-modules-header">
        <Typography.Text className="dashboard-modules-title">Modules</Typography.Text>
        <Typography.Text type="secondary" className="dashboard-modules-count">
          {moduleSources.length} of {MODULE_SOURCES.length}
        </Typography.Text>
      </div>

      <Row gutter={[16, 16]}>
        {moduleSources.map((source) => (
          <Col xs={24} sm={12} xl={8} key={source.key}>
            <ModuleCard
              icon={statIcons[source.key]}
              colors={MODULE_COLORS[source.key]}
              value={stats[source.key]}
              label={titleize(source.key)}
              loading={loading}
              onOpen={() => navigate(statRoutes[source.key])}
            />
          </Col>
        ))}
      </Row>

      <Card
        className="app-page-card dashboard-chart dashboard-chart-panel"
        variant="borderless"
        style={{ marginTop: 24 }}
      >
        <div className="dashboard-chart-header">
          <div>
            <Typography.Title level={4} style={{ marginBottom: 4 }}>
              Overview Graph
            </Typography.Title>
            <Typography.Text type="secondary">
              Quick snapshot of activity across the modules you manage.
            </Typography.Text>
          </div>
          <Button size="small" icon={<ReloadOutlined />} onClick={fetchStats} loading={loading}>
            Refresh
          </Button>
        </div>

        <div className="dashboard-chart-body">
          {loading ? (
            <div className="dashboard-empty dashboard-chart-empty">
              <Spin size="medium" />
            </div>
          ) : totalRecords === 0 ? (
            <Empty description="No data available" />
          ) : (
            <div className="dashboard-donut-wrap">
              <div className="dashboard-donut">
                <svg viewBox="0 0 220 220" className="dashboard-donut-svg" aria-hidden="true">
                  <circle
                    className="dashboard-donut-track"
                    cx={110}
                    cy={110}
                    r={DONUT_RADIUS}
                    strokeWidth={DONUT_STROKE}
                    fill="none"
                  />
                  {donutSlices.map((slice) => (
                    <circle
                      key={slice.key}
                      className={[
                        "dashboard-donut-slice",
                        slice.colorClass,
                        hoveredKey === slice.key ? "is-active" : "",
                        hoveredKey && hoveredKey !== slice.key ? "is-dimmed" : "",
                      ]
                        .filter(Boolean)
                        .join(" ")}
                      cx={110}
                      cy={110}
                      r={DONUT_RADIUS}
                      strokeWidth={DONUT_STROKE}
                      fill="none"
                      strokeLinecap="round"
                      strokeDasharray={slice.dasharray}
                      transform={`rotate(${slice.rotateDeg} 110 110)`}
                      tabIndex={0}
                      role="button"
                      aria-label={`${slice.name}: ${slice.value} (${slice.percentLabel})`}
                      onMouseEnter={() => setHoveredKey(slice.key)}
                      onMouseLeave={() => setHoveredKey(null)}
                      onFocus={() => setHoveredKey(slice.key)}
                      onBlur={() => setHoveredKey(null)}
                      onClick={openOverviewItem(slice.key)}
                      onKeyDown={overviewKeyHandler(slice.key)}
                    />
                  ))}
                </svg>
                <div className="dashboard-donut-center">
                  <span className="dashboard-donut-center-value">
                    {hoveredKey ? stats[hoveredKey] ?? 0 : totalRecords}
                  </span>
                  <span className="dashboard-donut-center-label">
                    {hoveredKey ? titleize(hoveredKey) : "total records"}
                  </span>
                </div>
              </div>

              <ul className="dashboard-donut-legend">
                {donutSlices.map((slice) => (
                  <li
                    key={slice.key}
                    className={`dashboard-donut-legend-row${
                      hoveredKey === slice.key ? " is-active" : ""
                    }`}
                    role="button"
                    tabIndex={0}
                    onMouseEnter={() => setHoveredKey(slice.key)}
                    onMouseLeave={() => setHoveredKey(null)}
                    onFocus={() => setHoveredKey(slice.key)}
                    onBlur={() => setHoveredKey(null)}
                    onClick={openOverviewItem(slice.key)}
                    onKeyDown={overviewKeyHandler(slice.key)}
                  >
                    <span className={`dashboard-donut-dot ${slice.colorClass}`} />
                    <span className="dashboard-donut-legend-label">{slice.name}</span>
                    <span className="dashboard-donut-legend-value">{slice.value}</span>
                    <span className="dashboard-donut-legend-percent">{slice.percentLabel}</span>
                  </li>
                ))}
              </ul>
            </div>
          )}
        </div>
      </Card>
    </div>
  );
};

export default Dashboard;
